import { createContext, useCallback, useContext, useMemo } from 'react';

import { useRouteLoaderData } from '@remix-run/react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import type { CurrentCommunityMember } from '~/interfaces/community';
import type { loader as communityLoader } from '~/routes/community';
import type { NectarPageBlock } from '~/utils/nectarPageBlock';
import { NECTAR_PAGE_BLOCK_GET_PARAM_NAME } from '~/utils/nectarTrackingFields';

import { PreviewContext } from '../_context/PreviewContext';
import type { CommunityPermissions } from '../_utils/permissions';
import { getCommunityPermissions } from '../_utils/permissions';
import { useCommunityAccess } from './useCommunityAccess';

export interface CommunityMembershipData {
  isMember: boolean;
  currentMember: CurrentCommunityMember | null;
  permissions: CommunityPermissions;
  openJoinModal: (pageBlock?: NectarPageBlock, destination?: string) => void;
  openSignInModal: () => void;
  isMessagesEnabled: boolean;
}

export const CommunityMembershipOverrideContext = createContext<{
  currentMember: CurrentCommunityMember | null;
} | null>(null);

export const useCommunityMembership = (): CommunityMembershipData => {
  const override = useContext(CommunityMembershipOverrideContext);
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const { handleOpenModal } = useGlobalModals();
  const { isCommunityAccessible } = useCommunityAccess();
  const isPreviewing = useContext(PreviewContext);
  const currentMember = override?.currentMember ?? layoutData?.currentMember ?? null;

  const permissions = useMemo(
    () => getCommunityPermissions(currentMember, { isCommunityAccessible, isPreviewing }),
    [currentMember, isCommunityAccessible, isPreviewing],
  );

  const openJoinModal = useCallback(
    (pageBlock?: NectarPageBlock, destination?: string) => {
      const pageBlockParams = pageBlock ? { [NECTAR_PAGE_BLOCK_GET_PARAM_NAME]: pageBlock } : undefined;
      handleOpenModal(
        'community_join',
        undefined,
        destination ? { ...pageBlockParams, redirect_to: destination } : pageBlockParams,
      );
    },
    [handleOpenModal],
  );

  const openSignInModal = useCallback(() => {
    handleOpenModal('community_login');
  }, [handleOpenModal]);

  return {
    isMember: Boolean(currentMember),
    currentMember,
    permissions,
    openJoinModal,
    openSignInModal,
    isMessagesEnabled: Boolean(layoutData?.isMessagesEnabled),
  };
};
